import * as React from 'react';
import { Button, FormInstance, TimePicker } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import { NamePath } from 'antd/lib/form/interface';
import { useTranslation } from 'react-i18next';

const FORMAT = 'HH:mm';
const DEFAULT_MAX_SLOTS = 3;

interface TimeSlotRow {
    key: number;
    value: [Moment, Moment] | null;
}

export interface TimeSlotListProps {
    form: FormInstance;
    formKey: NamePath;
    maxSlots?: number;
    value?: string[][];
}

const buildRowsFromSlots = (slots: string[][] | undefined | null): TimeSlotRow[] => {
    if (Array.isArray(slots) && slots.length) {
        const valid = slots.filter((slot) => slot?.length === 2);
        if (valid.length > 0) {
            return valid.map((slot, idx) => ({
                key: idx,
                value: [moment(slot[0], FORMAT), moment(slot[1], FORMAT)],
            }));
        }
    }
    return [{ key: 0, value: null }];
};

const TimeSlotList = ({
    form,
    formKey,
    maxSlots = DEFAULT_MAX_SLOTS,
    value,
}: TimeSlotListProps) => {
    const { t } = useTranslation();
    const isInternalChange = React.useRef(false);

    const [rows, setRows] = React.useState<TimeSlotRow[]>(
        () => buildRowsFromSlots(form.getFieldValue(formKey) as string[][]),
    );
    const [nextKey, setNextKey] = React.useState<number>(() => {
        const initial = form.getFieldValue(formKey);
        const slots = Array.isArray(initial) ? initial as string[][] : [];
        return Math.max(slots.filter((s: string[]) => s?.length === 2).length, 1);
    });

    React.useEffect(() => {
        if (isInternalChange.current) {
            isInternalChange.current = false;
            return;
        }
        const newRows = buildRowsFromSlots(value);
        setRows(newRows);
        setNextKey(Math.max(newRows.filter((r) => r.value !== null).length, 1));
    }, [value]);

    const toStringSlots = (currentRows: TimeSlotRow[]): string[][] => currentRows
        .filter((row) => row.value !== null)
        .map((row) => [
            row.value![0].format(FORMAT),
            row.value![1].format(FORMAT),
        ]);

    const syncToForm = (currentRows: TimeSlotRow[]) => {
        isInternalChange.current = true;
        form.setFields([{ name: formKey, value: toStringSlots(currentRows) }]);
    };

    const handleChange = (key: number, timeRange: [Moment, Moment] | null) => {
        const updatedRows = rows.map((row) => (row.key === key ? { ...row, value: timeRange } : row));
        setRows(updatedRows);
        syncToForm(updatedRows);
    };

    const handleRemove = (key: number) => {
        const updatedRows = rows.filter((row) => row.key !== key);
        setRows(updatedRows);
        syncToForm(updatedRows);
    };

    const handleAdd = () => {
        const newRow: TimeSlotRow = { key: nextKey, value: null };
        setRows((prev) => [...prev, newRow]);
        setNextKey((prev) => prev + 1);
    };

    return (
        <div>
            {rows.map((row) => (
                <div
                    key={row.key}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 8,
                        gap: 8,
                    }}
                >
                    <TimePicker.RangePicker
                        allowClear
                        format={FORMAT}
                        style={{ flex: 1 }}
                        value={row.value}
                        onChange={(val) => handleChange(row.key, val as [Moment, Moment] | null)}
                    />
                    {rows.length > 1 ? (
                        <MinusCircleOutlined
                            style={{
                                color: '#999',
                                fontSize: 16,
                                cursor: 'pointer',
                                flexShrink: 0,
                            }}
                            onClick={() => handleRemove(row.key)}
                        />
                    ) : null}
                </div>
            ))}
            {rows.length < maxSlots ? (
                <Button
                    type="link"
                    icon={<PlusOutlined />}
                    style={{ paddingLeft: 0 }}
                    onClick={handleAdd}
                >
                    {t('add_time_slot')}
                </Button>
            ) : null}
        </div>
    );
};

export default TimeSlotList;
